import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { Toaster, toast } from 'sonner';
import './styles/globals.css';
import './shared/stores/auth-store'; // ativa o setAccessTokenGetter side-effect
import { refreshRequest } from './features/auth/api';
import { router } from './routes/router';
import { criarPersistidorDoNavegador } from './shared/lib/copia-do-navegador';
import { clearPersistedQueryCache, prepararCopiaDoCacheNaEntrada } from './shared/lib/query-cache';
import { queryClient } from './shared/lib/query-client';
import { useAuthStore } from './shared/stores/auth-store';

/** Persister: salva queries do TanStack no localStorage. F5 restaura cache,
 *  user vê tela com dados antigos imediato (não há tela branca / spinner)
 *  enquanto bootstrapAuthLoop tenta refresh em background. Lição #80.
 *  Cópia reduzida + o que fazer quando não cabe: vide copia-do-navegador.ts. */
// PERSIST_CACHE_KEY vem de shared/lib/query-cache (fonte única — os fluxos de
// logout limpam essa MESMA chave; ver bug do vazamento cross-tenant lá).
const localStoragePersister = criarPersistidorDoNavegador(
  typeof window !== 'undefined' ? window.localStorage : undefined,
);

/**
 * Bootstrap em background com retry infinito de 5s. Mantém o user na URL
 * atual durante flap; toast persistente avisa "Sistema indisponível".
 * Quando refresh der OK, store atualiza e UI sai do estado degradado.
 *
 * Lição #80: F5 durante Redis off NÃO deve forçar logout. Cookie de refresh
 * continua válido — basta backend recuperar pra restaurar sessão.
 */
async function bootstrapAuthLoop(): Promise<void> {
  useAuthStore.getState().setBootstrapping(true);
  let degradedToastId: string | number | null = null;

  const showToast = (): void => {
    if (degradedToastId === null) {
      degradedToastId = toast.error('Sistema temporariamente indisponível', {
        description: 'Estamos com instabilidade. Reconectando…',
        duration: Number.POSITIVE_INFINITY,
      });
    }
  };

  // Mostra toast em 500ms se a 1ª tentativa de refresh ainda estiver rodando.
  // Se Redis OK (caso comum), refresh resolve em ~50ms e cancelamos antes de
  // aparecer — user nem vê. Se demorar (Redis off), toast aparece quase imediato.
  const earlyToastTimer = setTimeout(showToast, 500);

  while (true) {
    try {
      const result = await refreshRequest();
      clearTimeout(earlyToastTimer);
      useAuthStore.getState().setSession(result.accessToken, result.user);
      if (degradedToastId !== null) {
        toast.dismiss(degradedToastId);
        toast.success('Conexão restaurada');
      }
      return;
    } catch (err) {
      clearTimeout(earlyToastTimer);
      const isTimeout = err instanceof Error && err.message.includes('timeout');
      if (!isTimeout) {
        // Boot SEM sessão válida (refresh token expirado/revogado): qualquer
        // cache restaurado do localStorage é de uma sessão anterior — possivelmente
        // de OUTRO tenant. Limpa memória + localStorage pra não vazar no próximo
        // login neste browser. (Timeout NÃO cai aqui — é o caminho de Redis off,
        // mesmo user, onde queremos preservar o cache. Lição #80.)
        queryClient.clear();
        clearPersistedQueryCache();
        useAuthStore.getState().setBootstrapping(false);
        return;
      }
      showToast(); // Garante toast visível em retries subsequentes.
      await new Promise((r) => setTimeout(r, 5000));
    }
  }
}

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element not found');

// Antes do render (que restaura a cópia) e antes do socket (que pergunta o
// delta a partir da marca): cópia velha demais é descartada e, se a cópia
// estiver atrás da marca, a marca recua até ela. Vide query-cache.ts.
prepararCopiaDoCacheNaEntrada();

void bootstrapAuthLoop();

// Telemetria temporária pra mapear os gargalos de requisição do Atendimento
// com uso real. Envolve o fetch global, então precisa subir antes do render.
// Atribui a CAUSA de cada busca (foco/boot/invalidate) — sem isso, a maior
// parte das requisições fica sem origem identificada no relatório.

createRoot(rootEl).render(
  <StrictMode>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister: localStoragePersister,
        maxAge: 1000 * 60 * 60 * 24,
        // Buster invalida cache persistido quando bumped — usar quando shape
        // de query muda incompatível (ex: useQuery → useInfiniteQuery).
        // Bumpado em 2026-06-14: descarta listas paginadas presas no localStorage
        // (contatos/campanhas que restauravam todas as páginas roladas).
        // Bumpado em 2026-10-31: descarta UMA vez as cópias salvas antes da
        // correção da "conversa velha" — quem já tinha uma conversa com buraco
        // no meio guardada continuaria com ela (a cópia parece em dia porque as
        // últimas mensagens estão lá). Cada navegador começa limpo no primeiro
        // acesso, como depois de sair e entrar.
        buster: 'v2026-10-31',
        dehydrateOptions: {
          // NÃO persiste listas paginadas (scroll infinito) no localStorage —
          // senão o reload restaura todas as páginas roladas (ex: 450) em vez de
          // recomeçar na 1ª (50). Com gcTime:0 nessas queries, a navegação SPA
          // também recomeça. Vide contatos/campanhas.
          shouldDehydrateQuery: (query) => {
            const key0 = query.queryKey[0];
            if (key0 === 'contacts' || key0 === 'campaigns') return false;
            // Réplica do default: só persiste queries bem-sucedidas.
            return query.state.status === 'success';
          },
        },
      }}
    >
      <Toaster richColors position="top-center" closeButton duration={6000} />
      <RouterProvider router={router} />
    </PersistQueryClientProvider>
  </StrictMode>,
);
